import type { SalesReport, SalesTotals } from '../types/adminRequests'
import type { SalonInsights, WebshopInsights } from '../types/insights'
import type { LiabilitiesReport } from '../types/vouchers'

/**
 * Rapporten van een oudere server in de vorm van nu brengen. Velden die later aan een rapport zijn
 * toegevoegd kunnen bij een server die nog niet bijgewerkt is ontbreken; het beheer mag daar niet
 * op vastlopen. Lijsten worden leeg (het scherm verbergt dan dat blok), getallen 0, losse blokken
 * blijven weg (`stock`). Wat de server wél stuurt wint altijd. Puur, ook in de demo te gebruiken.
 */

type Loose<T> = { [K in keyof T]?: T[K] | null }

const list = <T>(v: T[] | null | undefined): T[] => (Array.isArray(v) ? v : [])
const num = (v: number | null | undefined): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0)

function totals(t: Loose<SalesTotals> | null | undefined): SalesTotals {
  const s = t ?? {}
  return {
    revenueExVatCents: num(s.revenueExVatCents),
    vatCents: num(s.vatCents),
    refundsCents: num(s.refundsCents),
    orders: num(s.orders),
    averageOrderCents: num(s.averageOrderCents),
    shippingExVatCents: num(s.shippingExVatCents),
    giftCardsCents: num(s.giftCardsCents),
    treatmentsExVatCents: num(s.treatmentsExVatCents),
  }
}

/** GET /admin/reports/sales: `byChannel` (kanalen), `stock` (voorraad) en de deeltotalen kwamen later. */
export function normalizeSalesReport(raw: SalesReport): SalesReport {
  const r = raw as unknown as Loose<SalesReport>
  return {
    totals: totals(r.totals as Loose<SalesTotals> | undefined),
    previous: totals(r.previous as Loose<SalesTotals> | undefined),
    series: list(r.series),
    byVatRate: list(r.byVatRate),
    byChannel: list(r.byChannel),
    byPerformer: list(r.byPerformer),
    byPaymentMethod: list(r.byPaymentMethod),
    topProducts: list(r.topProducts),
    ...(r.stock ? { stock: r.stock } : {}),
  }
}

/** GET /admin/reports/liabilities: elk blok apart, zodat een ontbrekend deel 0 wordt in plaats van een fout. */
export function normalizeLiabilities(raw: LiabilitiesReport): LiabilitiesReport {
  const r = raw as unknown as Loose<LiabilitiesReport>
  const v = (r.vouchers ?? {}) as Loose<LiabilitiesReport['vouchers']>
  const k = (r.kuren ?? {}) as Loose<LiabilitiesReport['kuren']>
  const c = (r.credit ?? {}) as Loose<LiabilitiesReport['credit']>
  return {
    on: typeof r.on === 'string' ? r.on : '',
    vouchers: { count: num(v.count), cents: num(v.cents) },
    kuren: { count: num(k.count), sessions: num(k.sessions), cents: num(k.cents) },
    credit: { customers: num(c.customers), cents: num(c.cents), treatments: num(c.treatments) },
  }
}

/** GET /admin/insights/salon: weekdagen en kuren kwamen later. */
export function normalizeSalonInsights(raw: SalonInsights): SalonInsights {
  const r = raw as unknown as Loose<SalonInsights>
  const k = (r.kuren ?? {}) as Loose<SalonInsights['kuren']>
  return {
    ...raw,
    byWeek: list(r.byWeek),
    byWeekday: list(r.byWeekday),
    treatments: list(r.treatments),
    kuren: { sold: num(k.sold), ended: num(k.ended), completed: num(k.completed), prepaidOpenCents: num(k.prepaidOpenCents) },
  }
}

/** GET /admin/insights/webshop: paren, stopredenen en cadeaubonnen kwamen later. */
export function normalizeWebshopInsights(raw: WebshopInsights): WebshopInsights {
  const r = raw as unknown as Loose<WebshopInsights>
  const s = (r.subscriptions ?? {}) as Loose<WebshopInsights['subscriptions']>
  const v = (r.vouchers ?? {}) as Loose<WebshopInsights['vouchers']>
  return {
    ...raw,
    products: list(r.products),
    pairs: list(r.pairs),
    funnel: Array.isArray(r.funnel) ? r.funnel : null,
    subscriptions: {
      active: num(s.active),
      started: num(s.started),
      cancelled: num(s.cancelled),
      reasons: list(s.reasons),
      failedPayments: num(s.failedPayments),
    },
    vouchers: { issuedCents: num(v.issuedCents), redeemedCents: num(v.redeemedCents), outstandingCents: num(v.outstandingCents) },
  }
}
